/**
 * SkillFoot — Personnalisation Officielle (flocage +2€ / badge +2€)
 *
 * Custom element <sf-flocage> living inside the product add-to-cart <form>.
 * - Toggle to enable/disable personalization (opens the panel).
 * - Flocage: two modes (radio) — custom name+number, or pick a team player.
 *   Team rosters are lazy-fetched from the sf-players-clubs.json asset on first
 *   interaction (hover/focus/toggle) and cached; if the fetch fails, the custom
 *   name/number fields still work (roster suggestions are a bonus).
 * - Badge: optional competition badge dropdown (Champions League, league badge,
 *   World Cup…) filtered by championship in the Liquid layer.
 * - On submit (capture phase), when enabled and something is chosen, posts a batch
 *   /cart/add.js with the maillot (carrying flocage/badge line item properties) plus,
 *   as needed, the +2€ "Flocage Officiel" variant and/or the +2€ "Badge de Compétition"
 *   variant (each tagged with `_lié_à`). Native single-item add is suppressed.
 *
 * Falls back gracefully: toggle off, or on but nothing filled => default theme flow.
 */
(function () {
  'use strict';

  /** Normalize a string for team matching: lowercase, strip accents, keep alnum. */
  function normalize(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  /**
   * Rosters live in the sf-players-clubs.json asset (too heavy to inline in every
   * page). Fetched once, on first interaction with a flocage widget, then cached.
   * On failure the promise resolves to null: the custom name/number fields keep
   * working, only the "player" suggestions are lost — and a later call retries.
   */
  var rostersCache = null;
  var rostersPromise = null;
  function loadRosters(url) {
    if (!rostersCache && window.SF_FLOCAGE_ROSTERS) rostersCache = window.SF_FLOCAGE_ROSTERS;
    if (rostersCache) return Promise.resolve(rostersCache);
    if (!url) return Promise.resolve(null);
    if (!rostersPromise) {
      rostersPromise = fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          rostersCache = data;
          window.SF_FLOCAGE_ROSTERS = data;
          return data;
        })
        .catch(function (err) {
          console.error('[sf-flocage] rosters fetch failed', err);
          rostersPromise = null; // allow a retry on the next interaction
          return null;
        });
    }
    return rostersPromise;
  }

  class SfFlocage extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.flocageVariantId = this.dataset.flocageVariantId;
      this.badgeVariantId = this.dataset.badgeVariantId;
      this.form = this.closest('form');

      this.enableEl = this.querySelector('[data-sf-enable]');
      this.panel = this.querySelector('[data-sf-panel]');
      this.modeRadios = Array.from(this.querySelectorAll('[data-sf-mode]'));
      this.fieldsPerso = this.querySelector('[data-sf-fields="perso"]');
      this.fieldsJoueur = this.querySelector('[data-sf-fields="joueur"]');
      this.nomEl = this.querySelector('[data-sf-nom]');
      this.numEl = this.querySelector('[data-sf-num]');
      this.basEl = this.querySelector('[data-sf-bas]');
      this.playerEl = this.querySelector('[data-sf-player]');
      this.badgeRadios = Array.from(this.querySelectorAll('[data-sf-badge-radio]'));
      this.modeJoueurLabel = this.querySelector('[data-sf-mode-joueur]');
      this.hintEl = this.querySelector('[data-sf-hint]');
      this.errEl = this.querySelector('[data-sf-error]');

      // Rosters are heavy: fetch them on first interaction only (hover/focus/toggle).
      this.roster = null;
      var kick = () => this._ensureRoster();
      this.addEventListener('pointerenter', kick);
      this.addEventListener('focusin', kick);

      this.enableEl.addEventListener('change', () => this._onToggle());
      this.modeRadios.forEach((r) => r.addEventListener('change', () => this._onModeChange()));
      if (this.playerEl) this.playerEl.addEventListener('change', () => this._onPlayerChange());
      this.badgeRadios.forEach((r) => r.addEventListener('change', () => this._updateHint()));
      if (this.nomEl) {
        this.nomEl.addEventListener('input', () => {
          this.nomEl.value = this.nomEl.value.toUpperCase();
          this._updateHint();
        });
      }
      if (this.numEl) {
        this.numEl.addEventListener('input', () => {
          // Digits only, 2 chars max (flocage number is bounded to 1–99).
          this.numEl.value = this.numEl.value.replace(/\D/g, '').slice(0, 2);
          this._clearError();
          this._updateHint();
        });
      }
      if (this.basEl) {
        this.basEl.addEventListener('input', () => {
          this.basEl.value = this.basEl.value.toUpperCase();
          this._updateHint();
        });
      }

      // Horizon's <product-form-component> handles add-to-cart via a click handler,
      // not a native form submit — so hook BOTH the submit (Enter key) and the click
      // of the add-to-cart button(s), in capture phase, to run before the theme.
      if (this.form) {
        this.form.addEventListener('submit', (e) => this._onSubmit(e), true);
        // Delegated (capture) so it survives morph replacing the button nodes
        // (variant changes on the PDP, product switches in the quick-add modal).
        this.form.addEventListener(
          'click',
          (e) => {
            var t = e.target instanceof Element ? e.target : null;
            if (t && t.closest('button[type="submit"], button[name="add"], [data-add-to-cart]')) {
              this._onSubmit(e);
            }
          },
          true
        );
      }

      this._onToggle();
    }

    /** Lazily load the roster data, then resolve + populate for the current team. */
    _ensureRoster() {
      var self = this;
      var src = normalize(this.dataset.teamSource);
      if (this._rosterFor === src) return; // already resolved (or in flight) for this team
      this._rosterFor = src;
      loadRosters(this.dataset.rostersUrl).then(function (rosters) {
        if (self._rosterFor !== src) return; // superseded (widget morphed onto another product)
        if (!rosters) {
          // Fetch failed: graceful fallback (custom fields only) + retry on next interaction.
          self._rosterFor = null;
          self.roster = null;
          self._populatePlayers();
          return;
        }
        self.roster = self._resolveRoster(rosters, src);
        self._populatePlayers();
      });
    }

    /** Find the matching roster by testing each key against the normalized team source. */
    _resolveRoster(rosters, src) {
      if (!src) return null;
      var keys = Object.keys(rosters);
      // Prefer the longest matching key (avoids "usa" matching inside unrelated words first).
      keys.sort(function (a, b) { return b.length - a.length; });
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].length >= 4 && src.indexOf(keys[i]) !== -1) {
          return rosters[keys[i]];
        }
      }
      return null;
    }

    _populatePlayers() {
      // Rebuild from scratch: quick-add can morph this widget onto another product.
      if (this.playerEl) {
        while (this.playerEl.options.length > 1) this.playerEl.remove(1);
      }
      if (!this.roster || !this.roster.length) {
        // No roster for this team: hide the "joueur" mode, keep custom only.
        if (this.modeJoueurLabel) this.modeJoueurLabel.hidden = true;
        return;
      }
      if (!this.playerEl) return;
      if (this.modeJoueurLabel) this.modeJoueurLabel.hidden = false;
      var frag = document.createDocumentFragment();
      this.roster.forEach(function (pl) {
        var opt = document.createElement('option');
        opt.value = pl.name + '|' + pl.number;
        opt.textContent = pl.name + ' — ' + pl.number;
        opt.dataset.name = pl.name;
        opt.dataset.number = pl.number;
        frag.appendChild(opt);
      });
      this.playerEl.appendChild(frag);
    }

    get enabled() {
      return !!(this.enableEl && this.enableEl.checked);
    }

    get mode() {
      var checked = this.modeRadios.find(function (r) { return r.checked; });
      return checked ? checked.dataset.sfMode : 'perso';
    }

    get badgeValue() {
      var r = this.badgeRadios.find(function (x) { return x.checked; });
      return r && r.value ? r.value : '';
    }

    _onToggle() {
      var on = this.enabled;
      this.panel.hidden = !on;
      if (on) this._ensureRoster();
      this._syncFieldState();
    }

    _onModeChange() {
      this._syncFieldState();
    }

    /** Enable only the active mode's inputs so disabled ones aren't submitted. */
    _syncFieldState() {
      var on = this.enabled;
      var mode = this.mode;
      var perso = on && mode === 'perso';
      var joueur = on && mode === 'joueur';

      if (this.fieldsPerso) this.fieldsPerso.hidden = !perso;
      if (this.fieldsJoueur) this.fieldsJoueur.hidden = !joueur;

      // Name/number stay enabled while the panel is open; in "joueur" mode they are
      // readonly (auto-filled). Validation is handled manually in _onSubmit so the
      // user can also order a badge alone without typing a name.
      if (this.nomEl) {
        this.nomEl.disabled = !on;
        this.nomEl.readOnly = joueur;
      }
      if (this.numEl) {
        this.numEl.disabled = !on;
        this.numEl.readOnly = joueur;
      }
      if (this.basEl) {
        this.basEl.disabled = !on;
      }
      if (this.playerEl) {
        this.playerEl.disabled = !joueur;
      }
      this.badgeRadios.forEach(function (r) { r.disabled = !on; });
      this._updateHint();
    }

    _onPlayerChange() {
      var opt = this.playerEl.selectedOptions[0];
      if (!opt || !opt.value) return;
      if (this.nomEl) this.nomEl.value = (opt.dataset.name || '').toUpperCase();
      if (this.numEl) this.numEl.value = opt.dataset.number || '';
      this._updateHint();
    }

    _showError(msg) {
      if (!this.errEl) {
        window.alert(msg);
        return;
      }
      this.errEl.textContent = msg;
      this.errEl.hidden = false;
    }

    _clearError() {
      if (this.errEl) this.errEl.hidden = true;
    }

    _updateHint() {
      if (!this.hintEl) return;
      if (!this.enabled) { this.hintEl.hidden = true; return; }
      var parts = [];
      var nomV = this.nomEl ? this.nomEl.value.trim() : '';
      var numV = this.numEl ? this.numEl.value.trim() : '';
      if (nomV || numV) {
        parts.push((nomV.toUpperCase() + ' ' + numV).trim());
      }
      if (this.basEl && this.basEl.value.trim() && this.mode !== 'joueur') {
        parts.push('Bas : ' + this.basEl.value.toUpperCase());
      }
      if (this.badgeValue) parts.push('Badge ' + this.badgeValue);
      if (parts.length) {
        this.hintEl.textContent = '✓ ' + parts.join('  ·  ');
        this.hintEl.hidden = false;
      } else {
        this.hintEl.hidden = true;
      }
    }

    _onSubmit(event) {
      if (!this.enabled) return; // default flow

      var numVal = this.numEl ? this.numEl.value.trim() : '';
      var hasBas = !!(this.basEl && this.basEl.value.trim() !== '' && this.mode !== 'joueur');
      // A number alone is a legit flocage (number-only printing) — never drop it silently.
      var hasFlocage = !!((this.nomEl && this.nomEl.value.trim() !== '') || numVal !== '' || hasBas);
      var badge = this.badgeValue;
      var hasBadge = badge !== '';

      // Flocage number must be between 1 and 99 (printers won't do "999").
      if (numVal !== '') {
        var n = parseInt(numVal, 10);
        if (!/^[0-9]{1,2}$/.test(numVal) || isNaN(n) || n < 1 || n > 99) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this._showError('Numéro de flocage invalide : choisis un numéro entre 1 et 99.');
          if (this.numEl) this.numEl.focus();
          return;
        }
      }
      this._clearError();

      // Panel open but nothing chosen -> let the native flow add just the maillot.
      if (!hasFlocage && !hasBadge) return;

      // We are taking over: stop the theme flow now (covers both click & submit).
      event.preventDefault();
      event.stopImmediatePropagation();

      // Guard against a click + submit both firing for the same gesture.
      if (this._busy) return;

      var variantInput = this.form.querySelector('input[name="id"]');
      var qtyInput = this.form.querySelector('[name="quantity"]');
      var maillotId = variantInput ? variantInput.value : null;
      var qty = qtyInput ? Number(qtyInput.value) || 1 : 1;
      if (!maillotId) {
        this._showError('Impossible d’ajouter au panier — recharge la page et réessaie.');
        return;
      }
      this._busy = true;

      // Properties carried by the maillot line.
      var props = { _perso: 'oui' };
      if (hasFlocage) {
        props['Flocage type'] = this.mode === 'joueur' ? 'Joueur' : 'Personnalisé';
        if (this.nomEl && this.nomEl.value.trim()) props['Flocage nom'] = this.nomEl.value.toUpperCase();
        if (this.numEl && this.numEl.value) props['Flocage numéro'] = this.numEl.value;
        if (hasBas) props['Flocage bas'] = this.basEl.value.toUpperCase();
      }
      if (hasBadge) props['Badge'] = badge;

      var items = [{ id: Number(maillotId), quantity: qty, properties: props }];

      if (hasFlocage && this.flocageVariantId) {
        var label = (props['Flocage nom'] || '') + ' ' + (props['Flocage numéro'] || '');
        if (props['Flocage bas']) label += ' / ' + props['Flocage bas'];
        items.push({
          id: Number(this.flocageVariantId),
          quantity: qty,
          properties: { _lié_à: maillotId, Flocage: label.trim() || 'Personnalisé' },
        });
      }
      if (hasBadge && this.badgeVariantId) {
        items.push({
          id: Number(this.badgeVariantId),
          quantity: qty,
          properties: { _lié_à: maillotId, Badge: badge },
        });
      }

      var self = this;
      var addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) {
            console.error('[sf-flocage] add error', res);
            self._showError(res.description || res.message || 'Impossible d’ajouter au panier. Réessaie.');
            return;
          }
          self._clearError();
          self._notifyCartUpdated();
        })
        .catch(function (err) {
          console.error('[sf-flocage] add failed', err);
          self._showError('Connexion impossible — vérifie ta connexion et réessaie.');
        })
        .finally(function () {
          self._busy = false;
        });
    }

    /** Refresh the cart UI (drawer / count) the same way the theme does. */
    _notifyCartUpdated() {
      fetch((window.Theme && Theme.routes && Theme.routes.cart_url ? Theme.routes.cart_url : '/cart') + '.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: cart, source: 'sf-flocage', data: { source: 'sf-flocage' } },
            })
          );
        })
        .catch(function () {
          window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
        });
    }
  }

  if (!customElements.get('sf-flocage')) {
    customElements.define('sf-flocage', SfFlocage);
  }
})();
